import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { formatMoney } from "@/lib/formatters"
import { cn } from "@/lib/utils"
import { toneDotClass, type StatusTone } from "@/lib/status-colors"
import { TillFigure } from "@/features/staff/till-figure"
import { ReportFigures, ReportList, ReportRow, ReportSection } from "@/features/staff/reports/report-block"
import { ReportDaySeries, ReportHourSeries } from "@/features/staff/reports/charts/sales-series"
import { ReportMethodShareBar } from "@/features/staff/reports/charts/method-share-bar"
import { ReportSalesSparkline } from "@/features/staff/reports/charts/sales-sparkline"
import { proportionInk } from "@/features/staff/reports/charts/proportion"
import {
  comparisonBasis,
  formatGrowth,
  formatHourLabel,
  growthTone,
  tReports,
  tSharedReports,
  type GrowthTone,
} from "@/features/staff/reports/format"
import type { PeriodSales } from "@/features/staff/reports/types"

/**
 * R1 — the only headline total on this screen.
 *
 * It is the one report in #313 that reconciles with the Z reports inside its
 * period to the centavo, and the three blocks below it deliberately do not. That
 * is why there is exactly one figure at `text-lg` here and none anywhere else:
 * two totals in the same visual weight, differing by a few pesos because one
 * counts payments and the other counts dishes, is how a reader stops trusting
 * both.
 */

/**
 * "Up" is `success` and "down" is `danger` only as directions of travel — the
 * mark says which way, never whether the restaurant is doing well. "New" takes
 * `reserved` rather than a neutral grey because a period with no baseline is
 * something to notice, not something missing.
 */
const GROWTH_TONE: Record<GrowthTone, StatusTone> = {
  up: "success",
  down: "danger",
  flat: "disabled",
  new: "reserved",
}

/**
 * The comparative, and the whole design of it is about not lying.
 *
 * The previous period is the same NUMBER of closed shifts, not last month — so
 * both sides normally bracket identical service counts. Both counts print beside
 * both totals anyway, unconditionally, because the reader has no other way to
 * know what the percentage is a percentage of. When the server says the counts
 * differ by more than one shift, the delta moves onto the average per shift and
 * the strip says so; a total-vs-total delta across 31 services and 24 is a
 * calendar-length lie wearing a percentage sign.
 *
 * A zero or absent baseline renders "nuevo". Growing from nothing is not a
 * percentage.
 */
function SalesComparisonStrip({ sales }: { sales: PeriodSales }) {
  const basis = comparisonBasis(sales)

  if (!basis) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="report-comparison-absent">
        {tReports("comparison_absent")}
      </p>
    )
  }

  return (
    <div className="space-y-1 rounded-md border px-4 py-3" data-testid="report-comparison">
      <div className="flex items-center gap-2">
        {/* The dot rule: the direction is the colour, the figure stays
            `text-foreground`. A percentage rendered in red at `text-sm` is
            already below contrast on a tinted surface, and the sign says it. */}
        <span
          aria-hidden="true"
          className={cn("shrink-0", toneDotClass(GROWTH_TONE[growthTone(basis.ratio)], "size-2"))}
        />
        <span
          className="text-sm font-semibold tabular-nums text-foreground"
          data-testid="report-comparison-delta"
        >
          {formatGrowth(basis.ratio)}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {tReports(
            basis.basis === "average_per_shift"
              ? "comparison_basis_average"
              : "comparison_basis_total"
          )}
        </span>
      </div>

      {/* Both shift counts, beside both totals, always. */}
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs tabular-nums text-muted-foreground">
        <span data-testid="report-comparison-current">
          {tReports("comparison_current", {
            amount: formatMoney(basis.currentAmount),
            shifts: tReports("shift_count", { count: basis.currentShiftCount }),
          })}
        </span>
        <span data-testid="report-comparison-previous">
          {tReports("comparison_previous", {
            amount: formatMoney(basis.previousAmount),
            shifts: tReports("shift_count", { count: basis.previousShiftCount }),
          })}
        </span>
      </div>

      {basis.basis === "average_per_shift" ? (
        <p className="text-xs text-muted-foreground" data-testid="report-comparison-normalized">
          {tSharedReports("notes.normalize_on_average")}
        </p>
      ) : null}
    </div>
  )
}

export function ReportSalesBlock({ sales }: { sales: PeriodSales }) {
  return (
    <Card density="compact" data-testid="report-sales">
      <CardHeader className="border-b">
        <CardTitle>{tReports("sales_title")}</CardTitle>
        <CardDescription>{tReports("sales_scope")}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <section className="space-y-2">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{tReports("sales_total_label")}</p>
            {/* The one `text-lg` on this screen, and the sparkline beside it
                rather than above it: the trend qualifies the total, it does not
                compete with it. */}
            <div className="flex items-center gap-3">
              <p
                className="text-lg font-semibold tabular-nums text-foreground"
                data-testid="report-sales-total"
              >
                {formatMoney(sales.subtotal)}
              </p>
              <ReportSalesSparkline rows={sales.byDay} />
            </div>
          </div>
          <SalesComparisonStrip sales={sales} />
        </section>

        <ReportFigures>
          <TillFigure
            label={tReports("transactions_label")}
            value={String(sales.transactionCount)}
            testId="report-sales-transactions"
          />
          <TillFigure
            label={tReports("average_ticket_label")}
            value={formatMoney(sales.averageTicket)}
            testId="report-sales-average-ticket"
          />
          <TillFigure
            label={tReports("shifts_label")}
            value={String(sales.shiftCount)}
            testId="report-sales-shifts"
          />
          <TillFigure
            label={tReports("average_per_shift_label")}
            value={formatMoney(sales.averagePerShift)}
            testId="report-sales-average-per-shift"
          />
          <TillFigure
            label={tReports("tips_label")}
            value={formatMoney(sales.tip)}
            hint={tReports("tips_hint")}
            testId="report-sales-tips"
          />
          <TillFigure
            label={tReports("busiest_hour_label")}
            value={sales.busiestHour ? formatHourLabel(sales.busiestHour.hour) : "—"}
            hint={sales.busiestHour ? formatMoney(sales.busiestHour.subtotal) : undefined}
            testId="report-sales-busiest-hour"
          />
        </ReportFigures>

        {sales.byMethod.length > 0 ? (
          <ReportSection title={tReports("by_method_title")}>
            {/* The proportion first, the figures under it. The bar answers "how
                is the money split" at a glance and carries no text; every exact
                amount stays in the rows, where it always was. */}
            <ReportMethodShareBar methods={sales.byMethod} />
            <ReportList testId="report-by-method">
              {sales.byMethod.map((method, index) => (
                <ReportRow
                  key={method.paymentMethod}
                  testId={`report-by-method-${method.paymentMethod}`}
                  label={method.paymentMethodLabel}
                  markClass={proportionInk(index)}
                  meta={tReports("transactions_count", { count: method.count })}
                  amount={formatMoney(method.collected)}
                />
              ))}
            </ReportList>
          </ReportSection>
        ) : null}

        <ReportDaySeries rows={sales.byDay} />
        <ReportHourSeries rows={sales.byHour} />
      </CardContent>
    </Card>
  )
}
